import clsx from 'clsx';
import { Theme, makeStyles } from '@material-ui/core/styles';

export type SmallInfoChipColor = 'blue' | 'red' | 'green' | 'yellow';

const useStyles = makeStyles<Theme>(theme => ({
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.5625rem',
    fontWeight: 600,
    lineHeight: 1,
    padding: '2px 5px',
    borderRadius: 4,
    backgroundColor: theme.palette.action.selected,
    letterSpacing: '0.02em',
    whiteSpace: 'nowrap',
    marginLeft: theme.spacing(0.5),
    verticalAlign: 'middle',
    pointerEvents: 'none',
    cursor: 'inherit',
  },
  blue: {
    color: theme.palette.info.main,
  },
  red: {
    color: theme.palette.error.main,
  },
  green: {
    color: theme.palette.success.main,
  },
  yellow: {
    color: theme.palette.warning.main,
  },
}));

export type SmallInfoChipProps = {
  /**
   * Custom label text. Can be any string.
   */
  label?: string;
  /**
   * Color variant of the chip. Defaults to "blue".
   */
  color?: SmallInfoChipColor;
  /**
   * Additional CSS class name
   */
  className?: string;
};

/**
 * A small chip/badge component for displaying information labels.
 * Designed to be tiny and unobtrusive, suitable for inline use in menus, lists, or other UI elements.
 *
 * @example
 * ```tsx
 * <SmallInfoChip label="Beta" color="blue" />
 * <SmallInfoChip label="New" color="green" />
 * <SmallInfoChip label="Error" color="red" />
 * ```
 */
export function SmallInfoChip({
  label = '',
  color = 'blue',
  className,
}: SmallInfoChipProps) {
  const classes = useStyles();

  return (
    <span className={clsx(classes.chip, classes[color], className)}>
      {label}
    </span>
  );
}
